import { Component, OnInit, Inject, DestroyRef, inject, HostListener } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';

import { ReadInvoiceService, ReadInvoiceParams } from '../../core/services/read-invoice.service';
import { CustomerProjectsService } from '../../core/services/customer-projects.service';
import { InvoiceOpService } from '../../core/services/invoice-op.service';
import { InvoiceItService, InvoiceItInsertParams } from '../../core/services/invoice-it.service';
import { PdfService } from '../../core/services/pdf.service';
import { FileSaverService } from '../../core/services/file-saver.service';
import { DialogService } from '../../core/services/dialog.service';
import { ModalService } from '../../core/services/modal.service';
import {
  EditInvoiceModalComponent,
  InvoiceData,
} from '../../shared/components/modal/edit-invoice-modal.component';
import {
  InvoiceReportItem,
  InvoiceReportSelectOptions,
  InvoiceStatement,
  InvoiceUpdateBody,
  CustomerOption,
} from './ng-invoice-reports.interfaces';

@Component({
  selector: 'app-ng-invoice-reports',
  standalone: false,
  template: require('./ng-invoice-reports.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-invoice-reports.component.scss').default],
})
export class NgInvoiceReportsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  openActionMenuKey: string | null = null;

  // Dates
  dateFrom: moment.Moment;
  dateTo: moment.Moment;
  opened1 = false;
  opened2 = false;

  // Data
  invoiceList: InvoiceReportItem[] = [];
  customers: CustomerOption[] = [];
  totAmount = 0;

  // Filters
  selectOptions: InvoiceReportSelectOptions = {};

  headers = ['Faktura Nr', 'Kund', 'Projekt', 'Datum', 'Period', 'Summa'];

  private normalizeStatementForStorage<T extends Record<string, any>>(statement: T, invoiceDate: string): T {
    const normalized = { ...statement, invoice_date: invoiceDate };
    delete normalized.inputdate;
    return normalized;
  }

  constructor(
    @Inject(ReadInvoiceService) private readInvoiceService: ReadInvoiceService,
    @Inject(CustomerProjectsService) private customerProjectsService: CustomerProjectsService,
    @Inject(InvoiceOpService) private invoiceOpService: InvoiceOpService,
    @Inject(InvoiceItService) private invoiceItService: InvoiceItService,
    @Inject(PdfService) private pdfService: PdfService,
    @Inject(FileSaverService) private fileSaverService: FileSaverService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(ModalService) private modalService: ModalService
  ) {
    // Date initialization logic from legacy
    if (moment().date() > 15) {
      this.dateFrom = moment().startOf('day').startOf('month');
    } else {
      this.dateFrom = moment().startOf('day').startOf('month').subtract(1, 'month');
    }
    this.dateTo = moment(this.dateFrom).add(1, 'month').subtract(1, 'days');
  }

  ngOnInit(): void {
    this.fetchCustProjects();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.report-row-action-menu')) {
      this.openActionMenuKey = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openActionMenuKey = null;
  }

  fetchCustProjects(): void {
    this.customerProjectsService.getCustomerProjects().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.customers = data;
      this.selectOptions.customers = data;
    });
  }

  updatePeriod(dir: number): void {
    if (dir === -1) {
      this.dateFrom.startOf('month').subtract(1, 'months');
      this.dateTo.startOf('month').subtract(1, 'days');
    } else {
      this.dateFrom.startOf('month').add(1, 'months');
      this.dateTo.startOf('month').add(2, 'months').subtract(1, 'days');
    }
    // Force new object references for inputs if needed, or just rely on mutation if moment object is shared
    // Angular change detection might need new reference or inputs
    this.dateFrom = moment(this.dateFrom);
    this.dateTo = moment(this.dateTo);
    this.dateFrom = moment(this.dateFrom);
    this.dateTo = moment(this.dateTo);
    // this.fetch(); // User requested manual fetch only
    // Wait, legacy HTML: <button ... ng-click="updatePeriod(- 1)">... <button ... ng-click="fetch()">
    // Ah, legacy updatePeriod does NOT call fetch. Just updates dates.
    // It's fine if I don't auto-fetch, but typically user expects it.
    // BUT, I should follow legacy behavior. Legacy invoiceReports.js updatePeriod does NOT call fetch().
    // So I will NOT call fetch() here.
    // Correcting: removed fetch() call.
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  fetch(): void {
    this.totAmount = 0;

    const cust = this.selectOptions.customer?.id ?? null;
    const proj = this.selectOptions.project?.id ?? null;

    const params: ReadInvoiceParams = {
      cust,
      proj,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.readInvoiceService.get<InvoiceReportItem>(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.invoiceList = data;
      let sum = 0;
      this.invoiceList.forEach((inv) => {
        sum += inv.amount;
      });
      this.totAmount = Math.round(sum * 100) / 100;
    });
  }

  // Helper for template
  getValue(head: string, inv: InvoiceReportItem): string | number | undefined {
    switch (head) {
      case 'Kund':
        return inv.customername;
      case 'Projekt':
        return inv.project_name;
      case 'Period':
        return inv.period;
      case 'Datum':
        return inv.invoice_date;
      case 'Summa':
        return inv.amount;
      default:
        return '';
    }
  }

  viewPdf(inv: InvoiceReportItem): void {
    const invoiceClone = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      period_start: inv.period_start ? moment(inv.period_start).format('YYYY-MM-DD') : undefined,
      period_stop: inv.period_stop ? moment(inv.period_stop).format('YYYY-MM-DD') : undefined,
    };

    this.pdfService.save(1, { statement: invoiceClone }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.openPdf(data.result);
    });
  }

  downloadPdf(inv: InvoiceReportItem): void {
    const invoiceClone = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      period_start: inv.period_start ? moment(inv.period_start).format('YYYY-MM-DD') : undefined,
      period_stop: inv.period_stop ? moment(inv.period_stop).format('YYYY-MM-DD') : undefined,
    };

    const statement: InvoiceStatement = JSON.parse(inv.statement);
    const statementType = statement.statementType === 'kredit' ? 'kreditfaktura ' : '';
    const filename = `Kits ${statementType}${inv.invoice_number} ${inv.customername}.pdf`;

    this.pdfService.save(1, { statement: invoiceClone }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.savePdf(data.result, filename);
    });
  }

  private b64toBlob(b64Data: string, contentType: string = '', sliceSize: number = 512): Blob {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  private openPdf(base64doc: string): void {
    const blob = this.b64toBlob(base64doc, 'application/pdf');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  private savePdf(base64doc: string, filename: string): void {
    const blob = this.b64toBlob(base64doc, 'application/pdf');
    this.fileSaverService.save(blob, filename);
  }

  async remove(inv: InvoiceReportItem, index: number): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'Är du säker?',
      body: 'Vill du ta bort fakturan?',
      confirmText: 'OK',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.invoiceOpService
      .remove({
        statement_id: inv.statement_id,
        invoice_number: inv.invoice_number,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.invoiceList.splice(index, 1);
        },
        error: async (error: { status?: number; error?: { error?: string } }) => {
          const body =
            error?.status === 409
              ? (error.error?.error || 'Det går inte att ta bort en faktura som redan har skickats till Fortnox. Skapa en kreditfaktura i stället.')
              : 'Det gick inte att ta bort fakturan.';

          await this.dialogService.alert({
            title: 'Operationen misslyckades',
            body,
            buttonText: 'OK',
            variant: 'danger',
          });
        },
      });
  }

  kredit(inv: InvoiceReportItem, index: number): void {
    const statement: InvoiceStatement = JSON.parse(inv.statement);

    if (statement.statementType === 'kredit') {
      this.dialogService.alert({
        title: 'Ej tillåtet',
        body: 'Det går inte att skapa kredit på en kreditfaktura.',
        buttonText: 'OK',
        variant: 'danger',
      });
      return;
    }

    statement.pris = -statement.pris;
    statement.statementType = 'kredit';

    if (statement.details) {
      statement.details.forEach((det) => {
        if (det.pris !== undefined) det.pris = -det.pris;
        det.price = -det.price;
      });
    }

    const invParams = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      amount: -inv.amount,
      statementType: 'kredit' as const,
      statement: JSON.stringify(this.normalizeStatementForStorage(statement as unknown as Record<string, any>, moment(inv.invoice_date).format('YYYY-MM-DD'))),
    };

    this.invoiceItService
      .insert(
        {
          cust_id: inv.customer_id,
          from: this.dateFrom.format('YYYY-MM-DD'),
          to: this.dateTo.format('YYYY-MM-DD'),
          groupMode: 'K',
        },
        invParams
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Legacy does not reload list here
      });
  }

  async edit(inv: InvoiceReportItem, index: number): Promise<void> {
    const updatedInv = (await this.modalService.show(EditInvoiceModalComponent, {
      headerText: 'Editera faktura',
      closeButtonText: 'Avbryt',
      actionButtonText: 'OK',
      inv: inv as unknown as InvoiceData,
    })) as InvoiceData | undefined;

    if (!updatedInv) {
      return;
    }

    updatedInv.statement = JSON.stringify(
      this.normalizeStatementForStorage(
        updatedInv.statement as unknown as Record<string, any>,
        updatedInv.invoice_date
      )
    ) as unknown as typeof updatedInv.statement;
    this.invoiceList[index] = updatedInv as unknown as InvoiceReportItem;
    this.updateInvoiceNo(updatedInv as unknown as InvoiceReportItem);
  }

  updateInvoiceNo(inv: InvoiceReportItem): void {
    const body: InvoiceUpdateBody = {
      invoice_date: inv.invoice_date,
      amount: inv.amount,
      statement: inv.statement,
    };

    this.invoiceOpService
      .update(
        {
          statement_id: inv.statement_id,
          invoice_number: inv.invoice_number,
        },
        body
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
